import { useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $getRoot,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
} from 'lexical';


// How long an incoming external `value` is ignored after this editor last
// emitted a local change. Several call sites (e.g. Announcements,
// ToolboxTalkGenerator) bind `value` straight to a live Apollo query result
// and save via a debounced mutation - without this guard, any refetch that
// lands before that mutation resolves hands back pre-edit content, which
// looks like a legitimate external update and silently reverts what the
// user just typed/pasted. Must comfortably exceed the longest debounce any
// consumer uses (2000ms as of writing).
const RESYNC_GUARD_MS = 3000;

function ControlledValuePlugin({ value, onChange, onBlur }) {
  const [editor] = useLexicalComposerContext();

  const lastJsonStringRef = useRef('');
  const lastLocalChangeAtRef = useRef(0);

  useEffect(() => {
    if (value == null || value === '') return;


    const incomingJsonString =
      typeof value === 'string' ? value : JSON.stringify(value);

    if (!incomingJsonString || incomingJsonString === lastJsonStringRef.current) {
      return;
    }

    if (Date.now() - lastLocalChangeAtRef.current < RESYNC_GUARD_MS) {
      return;
    }

    try {
      const incomingObj =
        typeof value === 'string' ? JSON.parse(value) : value;

      const nextEditorState = editor.parseEditorState(incomingObj);
      lastJsonStringRef.current = incomingJsonString;

      editor.setEditorState(nextEditorState);
    } catch (e) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        root.append($createParagraphNode());
      });
    }
  }, [editor, value]);


  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      const jsonObj = editorState.toJSON();
      const jsonString = JSON.stringify(jsonObj);

      if (jsonString !== lastJsonStringRef.current) {
        lastJsonStringRef.current = jsonString;
        lastLocalChangeAtRef.current = Date.now();
        onChange?.(jsonString);
      }
    });
  }, [editor, onChange]);

  useEffect(() => {
    return editor.registerCommand(
      BLUR_COMMAND,
      () => {
        onBlur?.(lastJsonStringRef.current);
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onBlur]);

  return null;
}

export default ControlledValuePlugin;